(() => {
  if (window.__hhsHeaderCapsule) return;
  window.__hhsHeaderCapsule = true;

  const controllers = new Map();
  const selector = '.header.hhs-header-capsule';
  const menuSelector = 'details[is="details-mega"]';

  function connect(header) {
    if (controllers.has(header)) return;

    const desktop = window.matchMedia('(min-width: 1024px)');
    const events = new AbortController();
    const observed = new Set();
    let frame = 0;
    let revealTimer = 0;
    let revealRequested = false;
    let activeMenus = [];
    let menus = [];
    let menusDirty = true;
    let lastSurfaceHeight = null;
    let destroyed = false;

    const schedule = () => {
      if (!destroyed && !frame) frame = requestAnimationFrame(update);
    };
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

    function update() {
      frame = 0;
      if (!header.isConnected) return destroy();

      if (menusDirty) {
        menus = [...header.querySelectorAll(menuSelector)];
        menusDirty = false;
      }
      const next = desktop.matches
        ? menus.filter(menu => menu.firstElementChild?.getAttribute('aria-expanded') === 'true')
        : [];
      const containers = next.map(menu => menu.lastElementChild?.firstElementChild).filter(Boolean);
      for (const element of observed) {
        if (!containers.includes(element)) {
          resize?.unobserve(element);
          observed.delete(element);
        }
      }
      for (const element of containers) {
        if (!observed.has(element)) {
          resize?.observe(element);
          observed.add(element);
        }
      }

      const inset = parseFloat(getComputedStyle(header).getPropertyValue('--hhs-capsule-block')) || 0;
      const closedHeight = Math.max(0, header.offsetHeight - inset * 2);
      const surfaceHeight = Math.max(closedHeight, ...next.map(menu => menu.lastElementChild?.firstElementChild?.offsetHeight || 0));
      const newMenu = next.some(menu => !activeMenus.includes(menu));

      if (surfaceHeight !== lastSurfaceHeight) {
        header.style.setProperty('--hhs-capsule-surface-height', `${surfaceHeight}px`);
        lastSurfaceHeight = surfaceHeight;
      }
      if (header.classList.contains('hhs-capsule-open') !== (next.length > 0)) {
        header.classList.toggle('hhs-capsule-open', next.length > 0);
      }
      for (const menu of menus) {
        const panel = menu.lastElementChild;
        const active = next.includes(menu);
        if (panel && panel.classList.contains('hhs-capsule-active') !== active) panel.classList.toggle('hhs-capsule-active', active);
      }

      if (next.length && (newMenu || revealRequested)) {
        clearTimeout(revealTimer);
        header.classList.add('hhs-capsule-revealing');
        revealTimer = setTimeout(() => header.classList.remove('hhs-capsule-revealing'), 650);
      } else if (!next.length) {
        clearTimeout(revealTimer);
        header.classList.remove('hhs-capsule-revealing');
      }
      activeMenus = next;
      revealRequested = false;
    }

    const mutations = new MutationObserver(records => {
      const relevant = records.filter(record => record.type === 'childList' ||
        (record.attributeName === 'aria-expanded' && record.target.matches('summary') && record.target.parentElement?.matches(menuSelector)) ||
        (record.attributeName === 'hidden' && record.target.closest?.(menuSelector)));
      if (!relevant.length) return;
      menusDirty ||= relevant.some(record => record.type === 'childList');
      revealRequested ||= relevant.some(record =>
        record.type === 'attributes' && record.attributeName === 'aria-expanded' &&
        record.target.matches('summary') && record.target.parentElement?.matches(menuSelector) &&
        record.target.getAttribute('aria-expanded') === 'true'
      );
      schedule();
    });

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      events.abort();
      mutations.disconnect();
      resize?.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(revealTimer);
      header.classList.remove('hhs-capsule-ready', 'hhs-capsule-open', 'hhs-capsule-revealing');
      header.style.removeProperty('--hhs-capsule-surface-height');
      header.querySelectorAll('.hhs-capsule-active').forEach(menu => menu.classList.remove('hhs-capsule-active'));
      controllers.delete(header);
    }

    controllers.set(header, destroy);
    header.classList.add('hhs-capsule-ready');
    mutations.observe(header, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-expanded', 'hidden'] });
    resize?.observe(header);
    if (!resize) window.addEventListener('resize', schedule, { signal: events.signal, passive: true });
    desktop.addEventListener('change', () => {
      if (!desktop.matches) {
        header.querySelectorAll(menuSelector).forEach(menu => { menu.open = false; });
      }
      schedule();
    }, { signal: events.signal });
    schedule();
  }

  function initialize(root = document) {
    for (const [header, destroy] of controllers) {
      if (!header.isConnected) destroy();
    }
    if (root.matches?.(selector)) connect(root);
    root.querySelectorAll(selector).forEach(connect);
  }

  customElements.whenDefined('details-mega').then(() => initialize());
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [header, destroy] of controllers) {
      if (event.target === header || event.target.contains(header)) destroy();
    }
  });
})();

